package one.healthium.paywall.ui

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Close
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.vector.rememberVectorPainter
import androidx.compose.ui.unit.dp
import androidx.compose.ui.util.fastForEach
import co.touchlab.kermit.Logger
import one.healthium.base.launcher.LocalPlatformLauncher
import one.healthium.domain.common.Animations
import one.healthium.domain.paywall.models.PaywallEmptyView
import one.healthium.domain.paywall.models.PaywallImageView
import one.healthium.domain.paywall.models.PaywallMainPlansView
import one.healthium.domain.paywall.models.PaywallOtherPlansView
import one.healthium.domain.paywall.models.PaywallPrivacyView
import one.healthium.domain.paywall.models.PaywallPurchaseButtonView
import one.healthium.domain.paywall.models.PaywallReviewsView
import one.healthium.domain.paywall.models.PaywallScreenView
import one.healthium.domain.paywall.models.PaywallSecuredView
import one.healthium.paywall.ui.components.PaywallEmptyView
import one.healthium.paywall.ui.components.PaywallImageView
import one.healthium.paywall.ui.components.PaywallMainPlansView
import one.healthium.paywall.ui.components.PaywallOtherPlansView
import one.healthium.paywall.ui.components.PaywallPrivacyView
import one.healthium.paywall.ui.components.PaywallPurchaseButtonView
import one.healthium.paywall.ui.components.PaywallReviewsView
import one.healthium.paywall.ui.components.PaywallSecuredView
import one.healthium.paywall.ui.models.ProductUiModel
import one.healthium.paywall.ui.scheduler.RemarketingEffect
import one.healthium.ui.common.event_producer.EventEffect
import one.healthium.ui.common.message.AlertUiMessageHandler
import one.healthium.ui.compose.components.AppLoadingScreen
import one.healthium.ui.compose.components.AppScaffold
import one.healthium.ui.compose.theme.black
import one.primehealth.analytics.compose.AppEventEffect
import one.primehealth.analytics.events.Events
import org.koin.compose.viewmodel.koinViewModel

interface PaywallEventDelegate {
    fun onPaywallPassed()
    fun onSignInClicked()
}

@Composable
fun PaywallScreen(
    paywallId: String? = null,
    eventDelegate: PaywallEventDelegate,
) {
    val viewModel = koinViewModel<PaywallViewModel>()

    val state by viewModel.state.collectAsState()
    val platformLauncher = LocalPlatformLauncher.current

    val currentPaywallId = state.paywall?.id
    AppEventEffect(currentPaywallId) {
        if (currentPaywallId != null) {
            logEvent(
                Events.PaywallImpression(
                    paywallVariant = currentPaywallId,
                    onboardingVariant = "v2000"
                )
            )
        }
    }

    LaunchedEffect(paywallId) {
        Logger.d("setPaywall: paywallId=$paywallId")
        viewModel.setPaywallId(paywallId)
    }

    EventEffect(viewModel.event) {
        when (it) {
            PaywallEvent.PaywallPassed -> eventDelegate.onPaywallPassed()
        }
    }

    RemarketingEffect(offers = state.paywallSet?.remarketingOffers)

    PaywallScreenContent(
        state = state,
        onProductOfferClicked = {
            viewModel.onProductOfferClicked(platformLauncher, it)
        },
        onPurchaseButtonClicked = {
            viewModel.onPurchaseButtonClicked(platformLauncher)
        },
        onRestoreClicked = viewModel::onRestoreClicked,
        onSignInClicked = eventDelegate::onSignInClicked,
        onSkipClicked = viewModel::onSkipClicked,
    )

    AlertUiMessageHandler(viewModel.uiMessages)
}

@Composable
internal fun PaywallScreenContent(
    state: PaywallUiState,
    onProductOfferClicked: (String) -> Unit,
    onPurchaseButtonClicked: () -> Unit,
    onRestoreClicked: () -> Unit,
    onSignInClicked: () -> Unit,
    onSkipClicked: () -> Unit,
) {
    AppScaffold {
        Box {
            if (state.screenModel != null) {
                PaywallViews(
                    state = state,
                    animations = state.screenModel.animations,
                    views = state.screenModel.views,
                    products = state.products.orEmpty(),
                    onProductOfferClicked = onProductOfferClicked,
                    onPurchaseButtonClicked = onPurchaseButtonClicked,
                    onRestoreClicked = onRestoreClicked,
                    onSignInClicked = onSignInClicked,
                )

                if (state.screenModel.hasSkip == true) {
                    IconButton(
                        modifier = Modifier
                            .statusBarsPadding()
                            .padding(horizontal = 12.dp),
                        onClick = onSkipClicked,
                    ) {
                        Icon(
                            painter = rememberVectorPainter(Icons.Filled.Close),
                            tint = black.copy(alpha = state.screenModel.skipAlpha ?: 1f),
                            contentDescription = null,
                        )
                    }
                }
            }

            AppLoadingScreen(
                visible = state.loading || state.paymentInProgress,
                dim = state.screenModel != null || state.paymentInProgress,
            )
        }
    }
}

@Composable
internal fun PaywallViews(
    state: PaywallUiState,
    animations: Animations?,
    views: List<PaywallScreenView>,
    products: List<ProductUiModel>,
    onProductOfferClicked: (String) -> Unit,
    onPurchaseButtonClicked: () -> Unit,
    onRestoreClicked: () -> Unit,
    onSignInClicked: () -> Unit,
) {
    Column(
        modifier = Modifier
            .fillMaxSize()
            .verticalScroll(rememberScrollState())
            .navigationBarsPadding()
    ) {
        views.fastForEach { paywallViewModel ->
            when (paywallViewModel) {
                is PaywallImageView -> PaywallImageView(paywallViewModel)
                is PaywallEmptyView -> PaywallEmptyView(paywallViewModel)
                is PaywallReviewsView -> PaywallReviewsView(paywallViewModel)
                is PaywallMainPlansView -> PaywallMainPlansView(
                    model = paywallViewModel,
                    products = products,
                    onClick = { onProductOfferClicked(it.product) },
                    selectedProductId = state.selectedProductId,
                )

                is PaywallOtherPlansView -> PaywallOtherPlansView(
                    model = paywallViewModel,
                    products = products,
                    onClick = { onProductOfferClicked(it.product) },
                )

                is PaywallPurchaseButtonView -> PaywallPurchaseButtonView(paywallViewModel, animations, onPurchaseButtonClicked)
                is PaywallSecuredView -> PaywallSecuredView(
                    model = paywallViewModel,
                    products = products,
                    selectedProductId = state.selectedProductId,
                )

                is PaywallPrivacyView -> PaywallPrivacyView(
                    model = paywallViewModel,
                    onRestoreClicked = onRestoreClicked,
                    onSignInClicked = onSignInClicked
                )
            }
        }
    }
}
